import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Check } from 'lucide-react';
import { messagesAPI, publicAPI, componentsAPI, usersAPI, conversationsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { formatLocalDate } from '../services/date';
import ImagePreviewModal from '../components/ImagePreviewModal';
import PdfThumbnail from '../components/PdfThumbnail';

const Messages = () => {
  const [loading, setLoading] = useState(false);
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [detailsRecipe, setDetailsRecipe] = useState(null);
  const [visibleUsers, setVisibleUsers] = useState([]);
  const [replyToUserId, setReplyToUserId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyToMessageId, setReplyToMessageId] = useState(null);
  const [replyConversationId, setReplyConversationId] = useState(null);
  const [showClone, setShowClone] = useState(false);
  const [cloneCtx, setCloneCtx] = useState({ msg: null, recipe: null });
  const [myComponents, setMyComponents] = useState([]);
  const [cloneSelects, setCloneSelects] = useState({ brass: '', bullet: '', primer: '', powder: '' });
  // Group chat state
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupMembers, setGroupMembers] = useState({});
  const [groupInitialMsg, setGroupInitialMsg] = useState('');
  const [conversations, setConversations] = useState([]);
  // Track which messages became visible to the user
  const [, setSeenDirectIds] = useState(() => new Set());
  const [, setSeenConvPairs] = useState(() => new Set()); // store `${convId}:${msgId}`
  const seenDirectIdsRef = useRef(new Set());
  const seenConvPairsRef = useRef(new Set());
  const [filePreview, setFilePreview] = useState(null);
  const inboxRef = useRef([]);
  const conversationsRef = useRef([]);

  const me = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('authUser') || '{}'); } catch { return {}; }
  }, []);
  const meId = me?.id;

  // Hidden 1:1 threads for current user (local-only)
  const [, forceHiddenTick] = useState(0);
  const getHiddenDirectSet = React.useCallback(() => {
    try {
      const raw = localStorage.getItem(`hiddenDirectThreads:${meId}`);
      const arr = Array.isArray(JSON.parse(raw)) ? JSON.parse(raw) : [];
      return new Set(arr.map((x) => Number(x)).filter((x) => Number.isFinite(x)));
    } catch { return new Set(); }
  }, [meId]);
  const hideDirectThread = (otherUserId) => {
    try {
      const set = getHiddenDirectSet();
      set.add(Number(otherUserId));
      localStorage.setItem(`hiddenDirectThreads:${meId}`, JSON.stringify(Array.from(set)));
      forceHiddenTick((v) => v + 1);
    } catch {}
  };

  // Unread helpers
  const computeUnread = React.useCallback((inboxItems, convs) => {
    try {
      const ug = (convs || []).some(cv => (cv.messages || []).some(m => !m.read_by_me && m.sender_user_id !== meId));
      return ug;
    } catch { return false; }
  }, [meId]);
  const setUnreadFlag = (val) => {
    try {
      localStorage.setItem('hasUnreadMessages', JSON.stringify(!!val));
      window.dispatchEvent(new CustomEvent('unreadMessagesChange', { detail: !!val }));
    } catch {}
  };

  const load = async () => {
    try {
      setLoading(true);
      const [usersRes, convRes] = await Promise.all([
        usersAPI.listVisible(''),
        conversationsAPI.listWithMessages().catch(()=>({ data: [] }))
      ]);
      const inboxItems = [];
      const sentItems = [];
      setInbox(inboxItems);
      inboxRef.current = inboxItems;
      setSent(sentItems);
      setVisibleUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      const convs = Array.isArray(convRes.data) ? convRes.data : [];
      setConversations(convs);
      conversationsRef.current = convs;
      try { setUnreadFlag(computeUnread([], convRes.data)); } catch {}
    } catch (e) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  // Keep refs in sync with latest state
  useEffect(() => { inboxRef.current = inbox; }, [inbox]);
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);

  const markRead = async (id) => {
    try {
      // Optimistic local updates with fresh arrays
      const newInbox = (inbox || []).map(mm => (mm.id === id ? { ...mm, read_by_me: true } : mm));
      setInbox(newInbox);
      setUnreadFlag(computeUnread(newInbox, conversations));
    } catch (e) {
      toast.error('Failed to mark as read');
    }
  };

  const openReplyModal = (otherUserId, parentMessageId = null) => {
    if (!otherUserId || otherUserId === meId) return;
    setReplyToUserId(otherUserId);
    setReplyToMessageId(parentMessageId);
    setReplyText('');
  };

  const sendReply = async () => {
    try {
      if (!replyText.trim()) return;
      if (replyConversationId) {
        // Mark parent read instantly
        if (replyToMessageId) {
          // Optimistic local update
          try {
            setConversations(prev => prev.map(cv => cv.id === replyConversationId ? ({
              ...cv,
              messages: (cv.messages || []).map(m => (m.id === replyToMessageId ? { ...m, read_by_me: true } : m))
            }) : cv));
            setUnreadFlag(computeUnread(inbox, conversations.map(cv => cv.id === replyConversationId ? ({
              ...cv,
              messages: (cv.messages || []).map(m => (m.id === replyToMessageId ? { ...m, read_by_me: true } : m))
            }) : cv)));
          } catch {}
          try { await conversationsAPI.markRead(replyConversationId, replyToMessageId); } catch {}
        }
        await conversationsAPI.sendMessage(replyConversationId, replyText.trim(), replyToMessageId || undefined);
      } else {
        if (!replyToUserId) return;
        // Upsert a 1:1 conversation with the other user
        const up = await conversationsAPI.upsert([replyToUserId]);
        const convId = up?.data?.id;
        if (!convId) throw new Error('Failed to prepare conversation');
        await conversationsAPI.sendMessage(convId, replyText.trim(), replyToMessageId || undefined);
      }
      // Automatically mark the original message as read when replying to it
      try {
        if (replyToMessageId && replyConversationId) {
          await conversationsAPI.markRead(replyConversationId, replyToMessageId);
        }
      } catch {}
      toast.success('Message sent');
      setReplyToUserId(null);
      setReplyConversationId(null);
      setReplyToMessageId(null);
      setReplyText('');
      load();
    } catch (e) {
      toast.error('Failed to send message');
    }
  };

  const cloneFromMessage = async (msg) => {
    try {
      if (!msg.recipe_id) return;
      const userId = msg.sender_user_id === meId ? msg.recipient_user_id : msg.sender_user_id;
      const res = await publicAPI.getRecipe(userId, msg.recipe_id);
      const recipe = res.data;
      const mineRes = await componentsAPI.getAll();
      const mine = Array.isArray(mineRes.data) ? mineRes.data : [];
      setMyComponents(mine);
      setCloneCtx({ msg, recipe });
      setCloneSelects({ brass: '', bullet: '', primer: '', powder: '' });
      setShowClone(true);
    } catch (e) {
      toast.error('Failed to clone recipe');
    }
  };

  const filteredOptions = (type) => {
    const lower = (s) => (s || '').toString().trim().toLowerCase();
    const comp = (cloneCtx.recipe?.components || []).find(rc => rc.type === type)?.component || {};
    const list = myComponents.filter(c => lower(c.component_type?.name) === lower(type));
    if (type === 'brass') {
      const cart = lower(comp.caliber);
      return list.filter(c => lower(c.caliber) === cart);
    }
    if (type === 'bullet') {
      const srcCi = parseFloat(comp.caliber_inches || '0');
      return list.filter(c => Math.abs(parseFloat(c.caliber_inches || '0') - srcCi) < 0.0005);
    }
    if (type === 'primer') {
      return list.filter(c => lower(c.firearm_type) === lower(comp.firearm_type) && lower(c.primer_size) === lower(comp.primer_size));
    }
    return list;
  };

  const confirmClone = async () => {
    try {
      const map = {};
      Object.entries(cloneSelects).forEach(([k, v]) => { if (v) map[k] = Number(v); });
      await messagesAPI.cloneFromMessage(cloneCtx.msg.id, map);
      toast.success('Recipe cloned from message');
      setShowClone(false);
    } catch (e) {
      const backendMsg = e?.response?.data?.message || e?.response?.data?.error;
      if (backendMsg) {
        toast.error(backendMsg);
      } else {
        toast.error('Failed to clone recipe');
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // IntersectionObserver to mark messages as "seen on screen" (only buffer, not mark read)
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const msgId = Number(el.getAttribute('data-msgid'));
          const convIdAttr = el.getAttribute('data-convid');
          const convId = convIdAttr ? Number(convIdAttr) : null;
          const isIncoming = el.getAttribute('data-incoming') === '1';
          if (!isIncoming || !msgId) return;
          if (convId) {
            const key = `${convId}:${msgId}`;
            setSeenConvPairs(prev => {
              if (prev.has(key)) return prev;
              const next = new Set(prev);
              next.add(key);
              return next;
            });
            if (!seenConvPairsRef.current.has(key)) {
              const copy = new Set(seenConvPairsRef.current);
              copy.add(key);
              seenConvPairsRef.current = copy;
            }
          } else {
            setSeenDirectIds(prev => {
              if (prev.has(msgId)) return prev;
              const next = new Set(prev);
              next.add(msgId);
              return next;
            });
            if (!seenDirectIdsRef.current.has(msgId)) {
              const copy = new Set(seenDirectIdsRef.current);
              copy.add(msgId);
              seenDirectIdsRef.current = copy;
            }
          }
        }
      });
    }, { threshold: 0.5 });

    const container = document.querySelector('[data-messages-root="1"]');
    if (container) {
      container.querySelectorAll('[data-observe-message="1"]').forEach(el => observer.observe(el));
    }
    return () => observer.disconnect();
  }, [inbox, sent, conversations]);

  // Flush seen messages as read when leaving Messages: on tab hide, before unload, or unmount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const flush = async () => {
      try {
        const direct = Array.from(seenDirectIdsRef.current);
        const conv = Array.from(seenConvPairsRef.current).map(k => {
          const [cid, mid] = k.split(':').map(Number);
          return { conversation_id: cid, message_id: mid };
        });
        if (direct.length === 0 && conv.length === 0) return;
        seenDirectIdsRef.current = new Set();
        seenConvPairsRef.current = new Set();
        await messagesAPI.markReadBulk(direct, conv);
        // Optimistic UI: update local state to clear unread badges
        if (direct.length) {
          setInbox(prev => prev.map(m => direct.includes(m.id) ? { ...m, read_by_me: true } : m));
        }
        if (conv.length) {
          setConversations(prev => prev.map(cv => ({
            ...cv,
            messages: (cv.messages || []).map(m => (conv.some(x => x.conversation_id === cv.id && x.message_id === m.id) ? { ...m, read_by_me: true } : m))
          })));
        }
        // Recompute navbar badge using latest state refs
        const nextInbox = (inboxRef.current || []).map(m => direct.includes(m.id) ? { ...m, read_by_me: true } : m);
        const nextConvs = (conversationsRef.current || []).map(cv => ({
          ...cv,
          messages: (cv.messages || []).map(m => (conv.some(x => x.conversation_id === cv.id && x.message_id === m.id) ? { ...m, read_by_me: true } : m))
        }));
        setUnreadFlag(computeUnread(nextInbox, nextConvs));
      } catch {}
    };
    const onHide = () => { if (document.visibilityState === 'hidden') flush(); };
    const onBeforeUnload = () => { flush(); };
    window.addEventListener('visibilitychange', onHide);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
      flush();
    };
  }, [computeUnread]);

  const openSharedRecipeDetails = async (msg) => {
    try {
      if (!msg.recipe_id) return;
      // Use message-scoped endpoint to bypass visibility for sender/recipient
      const res = await messagesAPI.getRecipe(msg.id);
      setDetailsRecipe(res.data);
    } catch (e) {
      toast.error('Failed to load recipe details');
    }
  };

  // removed unused closeDetails

  // Build threads grouped by the "other user" in each conversation
  const threads = useMemo(() => {
    const all = [...inbox, ...sent];
    // Deduplicate by id (in case of overlaps from null-FK handling in legacy data)
    const uniqueById = new Map();
    for (const m of all) uniqueById.set(m.id, m);
    let allUnique = Array.from(uniqueById.values());
    // Exclude group conversation messages from 1:1 threading
    allUnique = allUnique.filter(m => !m.conversation_id);
    // removed unused byId map
    // Build a quick user lookup from visible users
    const usersMap = new Map();
    try {
      (visibleUsers || []).forEach(u => {
        if ((u.email || '').toLowerCase() === 'system@local') return; // hide system user
        usersMap.set(u.id, {
          display: u.display_name || (u.first_name || u.last_name ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : null),
          email: u.email
        });
      });
    } catch {}
    // Start with 1:1 messages grouped by other party
    const map = new Map();
    for (const m of allUnique) {
      const isFromMe = m.sender_user_id === meId;
      const liveOtherId = isFromMe ? m.recipient_user_id : m.sender_user_id;
      const snapOtherId = isFromMe ? m.recipient_user_id_snapshot : m.sender_user_id_snapshot;
      const snapDisplay = isFromMe ? m.recipient_display_snapshot : m.sender_display_snapshot;
      const normLabel = (snapDisplay || '').toString().trim().toLowerCase();
      const key = (liveOtherId != null)
        ? `live:${liveOtherId}`
        : (snapOtherId != null ? `deleted:id:${snapOtherId}` : `deleted:label:${normLabel}`);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    }
    let threadList = [];
    for (const [threadKey, items] of map.entries()) {
      // Build a tree using parent_message_id but only within this thread
      const idsInThread = new Set(items.map(m => m.id));
      const parents = new Map();
      items.forEach(m => { if (m.parent_message_id) parents.set(m.id, m.parent_message_id); });
      const children = new Map();
      for (const m of items) {
        const pid = (m.parent_message_id && idsInThread.has(m.parent_message_id)) ? m.parent_message_id : 0;
        if (!children.has(pid)) children.set(pid, []);
        children.get(pid).push(m);
      }
      const ordered = [];
      const visit = (id, depth) => {
        const arr = children.get(id) || [];
        arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        for (const msg of arr) {
          ordered.push({ ...msg, _depth: Math.min(depth, 6) });
          visit(msg.id, depth + 1);
        }
      };
      visit(0, 0);
      // Determine thread title
      let otherDisplay = null;
      if (threadKey.startsWith('live:')) {
        const liveId = Number(threadKey.slice(5));
        const anyFromOther = ordered.find(x => x.sender_user_id === liveId && (x.sender || x.sender_user_id));
        otherDisplay = anyFromOther?.sender?.display_name || anyFromOther?.sender?.email;
        if (!otherDisplay) {
          const u = usersMap.get(liveId);
          otherDisplay = u?.display || u?.email || `User ${liveId}`;
        }
      } else {
        // Deleted thread: prefer snapshot on the opposite side of current user
        let cand = null;
        for (const msg of ordered) {
          if (msg.sender_user_id === meId) {
            cand = msg.recipient_display_snapshot || msg.recipient?.display_name || msg.recipient?.email;
          } else {
            cand = msg.sender_display_snapshot || msg.sender?.display_name || msg.sender?.email;
          }
          if (cand) break;
        }
        otherDisplay = cand || 'User (deleted)';
      }
      const lastAt = ordered[ordered.length - 1]?.created_at;
      const liveId = threadKey.startsWith('live:') ? Number(threadKey.slice(5)) : null;
      const otherUserMissing = !(liveId != null && usersMap.get(liveId));
      threadList.push({ key: threadKey, otherId: liveId, otherDisplay, otherUserMissing, lastAt, items: ordered });
    }
    // Append group conversations as separate threads
    try {
      conversations.forEach(conv => {
        const memberLabels = (conv.members || []).filter(m => !m.left_at).map(m => m.display_name || m.email);
        const title = memberLabels.length ? memberLabels.join(', ') : `Conversation ${conv.id}`;
        const msgs = (conv.messages || []).map(m => ({
          id: m.id,
          sender_user_id: m.sender_user_id,
          content: m.content,
          parent_message_id: m.parent_message_id,
          created_at: m.created_at,
          read_by_me: m.read_by_me,
          sender: m.sender,
          _depth: 0,
          sender_left: m.sender_left,
          is_group: true,
          conversation_id: conv.id,
          recipe_id: m.recipe_id,
          recipe_name: m.recipe_name,
        }));
        // Build nesting within group by parent_message_id
        const children = new Map();
        msgs.forEach(m => {
          const pid = m.parent_message_id || 0;
          if (!children.has(pid)) children.set(pid, []);
          children.get(pid).push(m);
        });
        const ordered = [];
        const visit = (id, depth) => {
          const arr = children.get(id) || [];
          arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          for (const msg of arr) {
            ordered.push({ ...msg, _depth: Math.min(depth, 6) });
            visit(msg.id, depth + 1);
          }
        };
        visit(0, 0);
        const lastAt = ordered[ordered.length - 1]?.created_at;
        threadList.push({ key: `conv:${conv.id}`, otherId: null, otherDisplay: title, otherUserMissing: false, lastAt, items: ordered, is_group: true, conv });
      });
    } catch {}
    // Filter out hidden 1:1 threads
    try {
      const hidden = getHiddenDirectSet();
      threadList = threadList.filter(t => !(t.key.startsWith('live:') && hidden.has(Number(t.key.slice(5)))));
    } catch {}
    threadList.sort((a, b) => new Date(b.lastAt) - new Date(a.lastAt));
    return threadList;
  }, [inbox, sent, meId, visibleUsers, conversations, getHiddenDirectSet]);

  return (
    <div className="space-y-6" data-messages-root="1">
      {loading && (
        <div className="text-sm text-gray-500">Loading…</div>
      )}

      {!loading && threads.length === 0 && (
        <div className="text-sm text-gray-500">No conversations</div>
      )}

      <div className="flex justify-end">
        <button className="btn btn-secondary" onClick={() => setShowGroupModal(true)}>Group Chat</button>
      </div>

      {!loading && threads.map(thread => (
        <div key={thread.key} className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-lg font-semibold">
              <div className="flex flex-wrap gap-1 items-center">
                {(() => {
                  const members = thread.is_group
                    ? (thread.conv?.members || []).filter(m => !m.left_at)
                    : ([{ id: meId, display_name: 'Me', email: me?.email }, { id: thread.otherId, display_name: thread.otherDisplay, email: null }]);
                  const activeCount = members.length;
                  return members.map((m, i) => (
                    <span key={`${m.id}-${i}`} className={m.id === meId ? 'font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded' : 'text-gray-800'}>
                      {m.id === meId ? 'Me' : (m.display_name || m.email || (m.id != null ? `User ${m.id}` : 'User'))}{i < (activeCount - 1) ? ',' : ''}
                    </span>
                  ));
                })()}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {thread.is_group ? (
                <>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => { setReplyConversationId(thread.conv.id); setReplyToMessageId(null); setReplyToUserId(null); setReplyText(''); }}
                  >New Message</button>
                  <button className="btn btn-sm" onClick={async () => { try { await conversationsAPI.leave(thread.conv.id); load(); } catch {} }}>Leave Chat</button>
                </>
              ) : (
                !thread.otherUserMissing && (
                  <>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => openReplyModal(thread.otherId, null)}
                    >New Message</button>
                    <button className="btn btn-sm" onClick={() => hideDirectThread(thread.otherId)}>Leave Chat</button>
                  </>
                )
              )}
            </div>
          </div>
          {thread.otherUserMissing && (
            <div className="mb-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">User no longer exists</div>
          )}
          <div className="space-y-2">
            {thread.items.map(m => {
              const isMe = m.sender_user_id === meId;
              const isUnread = !isMe && !m.read_by_me;
              const bubbleBase = isMe ? 'bg-blue-100 text-blue-900' : (isUnread ? 'bg-yellow-100 text-yellow-900' : 'bg-gray-100 text-gray-900');
              const bubble = m.sender_left && !isMe ? 'bg-gray-200 text-gray-500' : bubbleBase;
              const depthPx = Math.min((m._depth || 0) * 16, 96); // 16px per level, max 96px
              return (
                <div
                  key={`${m.id}`}
                  className="flex flex-col"
                  style={{ paddingLeft: `${depthPx}px` }}
                  data-observe-message="1"
                  data-msgid={m.id}
                  data-convid={thread.is_group ? thread.conv.id : ''}
                  data-incoming={!isMe ? '1' : '0'}
                >
                  <div className={`relative rounded p-3 ${bubble}`}>
                    <div className="flex items-center gap-2 text-xs mb-1">
                      <span
                        className={
                          isMe
                            ? 'font-semibold text-blue-700'
                            : (m.sender_left
                                ? 'font-semibold text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded'
                                : 'font-semibold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded')
                        }
                      >
                        {isMe ? 'Me' : (thread.is_group ? (m.sender?.display_name || m.sender?.email || 'User') : (m.sender?.display_name || m.sender?.email || ''))}
                      </span>
                      <span className="text-gray-600">{new Date(m.created_at).toLocaleString()}</span>
                    </div>
                    <div className="whitespace-pre-wrap text-sm">{m.content}</div>
                    <div className="mt-2 text-xs flex justify-between items-center">
                      <div className="text-gray-600">
                        {m.recipe_id && <span>Recipe: {m.recipe_name || m.recipe_id}</span>}
                        {m.folder === 'inbox' && m.read_at && <span className="ml-2">Read: {new Date(m.read_at).toLocaleString()}</span>}
                      </div>
                      {!thread.otherUserMissing && !thread.is_group && (
                        <div className="space-x-2">
                          {m.recipe_id && !isMe && (
                            <button className="btn btn-sm btn-primary" onClick={() => cloneFromMessage(m)}>Clone</button>
                          )}
                          {m.recipe_id && (
                            <button className="btn btn-sm btn-secondary" onClick={() => openSharedRecipeDetails(m)}>Details</button>
                          )}
                          {!isMe && (
                            <button className="btn btn-sm" onClick={() => openReplyModal(m.sender_user_id, m.id)}>Reply</button>
                          )}
                          {m.folder === 'inbox' && !m.read_by_me && (
                            <button className="btn btn-sm" onClick={() => markRead(m.id)}>Mark Read</button>
                          )}
                        </div>
                      )}
                      {(thread.is_group || true) && (
                        <div className="space-x-2">
                          {m.recipe_id && !isMe && (
                            <button className="btn btn-sm btn-primary" onClick={() => cloneFromMessage(m)}>Clone</button>
                          )}
                          {m.recipe_id && (
                            <button className="btn btn-sm btn-secondary" onClick={() => openSharedRecipeDetails(m)}>Details</button>
                          )}
                          {thread.is_group && !isMe && !m.sender_left && (
                            <button className="btn btn-sm" onClick={() => { setReplyConversationId(thread.conv.id); setReplyToMessageId(m.id); setReplyToUserId(null); setReplyText(''); }}>Reply</button>
                          )}
                          {thread.is_group && !isMe && !m.read_by_me && (
                            <button className="btn btn-sm" onClick={async () => { try {
                              // Optimistic local update
                              const newConvs = conversations.map(cv => cv.id === thread.conv.id ? ({ ...cv, messages: (cv.messages || []).map(x => (x.id === m.id ? { ...x, read_by_me: true } : x)) }) : cv);
                              setConversations(newConvs);
                              setUnreadFlag(computeUnread(inbox, newConvs));
                              await conversationsAPI.markRead(thread.conv.id, m.id);
                            } catch {} }}>Mark Read</button>
                          )}
                        </div>
                      )}
                    </div>
                    {isMe && m.read_at && (
                      <div className="absolute right-1 bottom-1 text-green-600">
                        <Check className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {detailsRecipe && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-900">{detailsRecipe.name}</h3>
              <button onClick={() => setDetailsRecipe(null)} className="text-gray-400 hover:text-gray-600 text-3xl leading-none p-1" aria-label="Close">×</button>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              <div>
                <div className="text-gray-600">Cartridge</div>
                <div className="font-medium">{detailsRecipe.cartridge || '—'}</div>
              </div>
              <div>
                <div className="text-gray-600">Created</div>
                <div className="font-medium">{detailsRecipe.created_at ? formatLocalDate(detailsRecipe.created_at) : '—'}</div>
              </div>
            </div>
            {/* Dimensions and Crimp */}
            {(detailsRecipe.case_trim_length || detailsRecipe.cartridge_overall_length || detailsRecipe.seating_depth || detailsRecipe.crimp_type || detailsRecipe.crimp_strength) && (
              <div className="mb-4 grid grid-cols-2 gap-4 text-sm">
                {detailsRecipe.case_trim_length && (
                  <div>
                    <div className="text-gray-600">Case Trim Length</div>
                    <div className="font-medium">{detailsRecipe.case_trim_length}"</div>
                  </div>
                )}
                {detailsRecipe.cartridge_overall_length && (
                  <div>
                    <div className="text-gray-600">Overall Length</div>
                    <div className="font-medium">{detailsRecipe.cartridge_overall_length}"</div>
                  </div>
                )}
                {detailsRecipe.seating_depth && (
                  <div>
                    <div className="text-gray-600">Seating Depth</div>
                    <div className="font-medium">{detailsRecipe.seating_depth}"</div>
                  </div>
                )}
                {detailsRecipe.crimp_type && (
                  <div>
                    <div className="text-gray-600">Crimp Type</div>
                    <div className="font-medium">{detailsRecipe.crimp_type}</div>
                  </div>
                )}
                {typeof detailsRecipe.crimp_strength !== 'undefined' && detailsRecipe.crimp_strength !== null && (
                  <div>
                    <div className="text-gray-600">Crimp Strength</div>
                    <div className="font-medium">{detailsRecipe.crimp_strength}</div>
                  </div>
                )}
              </div>
            )}
            {/* Self-cannelure is displayed under Bullet section below */}
            {detailsRecipe.description && (
              <div className="mb-4">
                <div className="text-gray-600 text-sm">Description</div>
                <div className="text-sm">{detailsRecipe.description}</div>
              </div>
            )}
            {detailsRecipe.notes && (
              <div className="mb-4">
                <div className="text-gray-600 text-sm">Notes</div>
                <div className="text-sm">{detailsRecipe.notes}</div>
              </div>
            )}
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Components</div>
              <div className="space-y-2">
                {(detailsRecipe.components || []).map((rc, idx) => {
                  const c = rc.component || {};
                  return (
                    <div key={idx} className="p-3 bg-gray-50 rounded text-sm">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 text-base">
                            {c.brand} {c.model}
                          </div>
                          <div className={`text-xs uppercase tracking-wide font-medium ${
                            (rc.type || '').toLowerCase() === 'bullet' ? 'text-blue-600' :
                            (rc.type || '').toLowerCase() === 'primer' ? 'text-red-600' :
                            (rc.type || '').toLowerCase() === 'powder' ? 'text-orange-600' :
                            (rc.type || '').toLowerCase() === 'brass' ? 'text-yellow-600' : 'text-gray-500'
                          }`}>
                            {rc.type}
                          </div>
                        </div>
                        <div className="text-right text-gray-700">{rc.quantity} {rc.unit}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-gray-600">
                        {/* Common */}
                        {c.caliber && <div>Caliber: {c.caliber}</div>}
                        {c.description && <div className="col-span-2">{c.description}</div>}
                        {/* Brass-specific */}
                        {rc.type?.toLowerCase() === 'brass' && (
                          <>
                            {c.head_stamp && <div>Head Stamp: {c.head_stamp}</div>}
                            {c.primer_pocket_size && <div>Primer Pocket Size: {c.primer_pocket_size}</div>}
                            {c.primer_pocket_size === 'Large' && c.primer_pocket_type && (
                              <div>Large Pocket Type: {c.primer_pocket_type}</div>
                            )}
                          </>
                        )}
                        {/* Primer-specific */}
                        {rc.type?.toLowerCase() === 'primer' && (
                          <>
                            {c.firearm_type && <div>Firearm Type: {c.firearm_type}</div>}
                            {c.primer_size && <div>Primer Size: {c.primer_size}</div>}
                            {typeof c.magnum !== 'undefined' && <div>Magnum: {c.magnum ? 'Yes' : 'No'}</div>}
                          </>
                        )}
                        {/* Bullet-specific */}
                        {rc.type?.toLowerCase() === 'bullet' && (
                          <>
                            {c.weight_grains && <div>Weight: {c.weight_grains} gr</div>}
                            {c.length_inches && <div>Length: {c.length_inches}"</div>}
                            {c.caliber_inches && <div>Bullet Caliber: {c.caliber_inches}"</div>}
                            {(() => {
                              const selfCan = !!detailsRecipe.self_cannelured;
                              const hasCannelure = !!c.cannelured;
                              if (!selfCan && !hasCannelure) return null;
                              const rawDepth = hasCannelure ? c.cannelure_depth : detailsRecipe.cannelure_depth;
                              const depthNum = (rawDepth !== undefined && rawDepth !== null && `${rawDepth}`.trim() !== '') ? Number(rawDepth) : null;
                              const depthPart = Number.isFinite(depthNum) ? ` • Depth ${depthNum}"` : '';
                              return (
                                <div>
                                  Cannelure: {selfCan ? 'Self-cannelured' : 'Factory'}{depthPart}
                                </div>
                              );
                            })()}
                            {c.core_material && <div>Core: {c.core_material}</div>}
                            {c.covering && <div>Covering: {c.covering}</div>}
                            {c.shape && <div>Shape: {c.shape}</div>}
                            {c.intended_cartridges && <div className="col-span-2">Intended: {c.intended_cartridges}</div>}
                          </>
                        )}
                        {/* Powder-specific */}
                        {rc.type?.toLowerCase() === 'powder' && (
                          <>
                            {c.description && <div className="col-span-2">{c.description}</div>}
                          </>
                        )}
                      </div>
                      {/* Component Images */}
                      {(c.images || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {c.images.map(file => {
                            const isPdf = (file.filename || '').toLowerCase().endsWith('.pdf');
                            return (
                              <div key={file.id} className="relative">
                                {isPdf ? (
                                  <div
                                    className="w-12 h-12 cursor-pointer"
                                    onClick={() => setFilePreview({ id: file.id, url: file.url, filename: file.filename, thumbnailUrl: file.thumbnail_url })}
                                  >
                                    <PdfThumbnail
                                      url={file.url}
                                      alt={file.filename}
                                      className="w-12 h-12 object-cover rounded border border-gray-200"
                                    />
                                  </div>
                                ) : (
                                  <img
                                    src={file.thumbnail_url || file.url}
                                    alt={file.filename}
                                    className="w-12 h-12 object-cover rounded border border-gray-200 cursor-pointer"
                                    onClick={() => setFilePreview({ id: file.id, url: file.url, filename: file.filename, thumbnailUrl: file.thumbnail_url })}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">Velocity by Firearm</div>
              {(detailsRecipe.velocity_by_firearm || []).length > 0 ? (
                <div className="space-y-1 text-xs text-gray-700">
                  {detailsRecipe.velocity_by_firearm.map((vs, i) => (
                    <div key={i}>{vs.label}: Avg {vs.average}, ES {vs.es ?? '—'}, SD {vs.sd ?? '—'}, N {vs.count}</div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-gray-500">No velocity stats available</div>
              )}
            </div>
            <div className="mt-4">
              <div className="text-sm font-medium text-gray-700 mb-1">Owner Avg Cost per Load</div>
              <div className="text-sm">{detailsRecipe.owner_average_cost_per_load != null ? `$${Number(detailsRecipe.owner_average_cost_per_load).toFixed(2)}` : 'NA'}</div>
            </div>
            {/* Recipe Images */}
            {(detailsRecipe.images || []).length > 0 && (
              <div className="mt-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Recipe Images</div>
                <div className="flex flex-wrap gap-3">
                  {detailsRecipe.images.map(file => {
                    const isPdf = (file.filename || '').toLowerCase().endsWith('.pdf');
                    return (
                      <div key={file.id} className="relative group">
                        {isPdf ? (
                          <div
                            className="w-20 h-20 cursor-pointer"
                            onClick={() => setFilePreview({ id: file.id, url: file.url, filename: file.filename, thumbnailUrl: file.thumbnail_url })}
                          >
                            <PdfThumbnail
                              url={file.url}
                              alt={file.filename}
                              className="w-20 h-20 object-cover rounded border border-gray-200"
                            />
                          </div>
                        ) : (
                          <img
                            src={file.thumbnail_url || file.url}
                            alt={file.filename}
                            className="w-20 h-20 object-cover rounded border border-gray-200 cursor-pointer"
                            onClick={() => setFilePreview({ id: file.id, url: file.url, filename: file.filename, thumbnailUrl: file.thumbnail_url })}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {(replyToUserId || replyConversationId) && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-3">Reply</h3>
            <textarea
              className="input h-28"
              placeholder="Write your message…"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-3">
              <button className="btn" onClick={() => { setReplyToUserId(null); setReplyConversationId(null); setReplyToMessageId(null); setReplyText(''); }}>Cancel</button>
              <button className="btn btn-primary" onClick={sendReply}>Send</button>
            </div>
          </div>
        </div>
      )}
      {showClone && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-3">Clone Recipe - Map Components</h3>
            <p className="text-sm text-gray-600 mb-4">Select your existing components to use. Leave empty to import from the source recipe.</p>
            {['brass','bullet','primer','powder'].map((t) => (
              <div key={t} className="mb-3">
                <label className="label capitalize">{t}</label>
                <select className="input" value={cloneSelects[t]} onChange={(e)=>setCloneSelects(s => ({...s, [t]: e.target.value}))}>
                  <option value="">Import from source</option>
                  {filteredOptions(t).map(c => (
                    <option key={c.id} value={c.id}>{c.brand} {c.model}</option>
                  ))}
                </select>
              </div>
            ))}
            <div className="flex justify-end gap-2 mt-4">
              <button className="btn" onClick={()=>setShowClone(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmClone}>Clone</button>
            </div>
          </div>
        </div>
      )}
      {showGroupModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-3">Start Group Chat</h3>
            <div className="max-h-60 overflow-y-auto border rounded p-2 mb-3">
              {(visibleUsers || []).filter(u => u.id !== meId).map(u => (
                <label key={u.id} className="flex items-center justify-between border-b last:border-b-0 py-2">
                  <div>
                    <div className="font-medium">{u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}</div>
                    <div className="text-xs text-gray-600">{u.email}</div>
                  </div>
                  <input type="checkbox" checked={!!groupMembers[u.id]} onChange={() => setGroupMembers(prev => ({ ...prev, [u.id]: !prev[u.id] }))} />
                </label>
              ))}
            </div>
            <textarea className="input h-24" placeholder="First message..." value={groupInitialMsg} onChange={(e) => setGroupInitialMsg(e.target.value)} />
            <div className="flex justify-end gap-2 mt-3">
              <button className="btn" onClick={() => { setShowGroupModal(false); setGroupMembers({}); setGroupInitialMsg(''); }}>Cancel</button>
              <button className="btn btn-primary" onClick={async () => {
                try {
                  const ids = Object.keys(groupMembers).filter(id => groupMembers[id]).map(id => Number(id));
                  if (ids.length === 0 || !groupInitialMsg.trim()) return;
                  await conversationsAPI.create(ids, groupInitialMsg.trim());
                  setShowGroupModal(false);
                  setGroupMembers({});
                  setGroupInitialMsg('');
                  load();
                } catch {}
              }}>Create</button>
            </div>
          </div>
        </div>
      )}
      <ImagePreviewModal
        id={filePreview?.id}
        url={filePreview?.url}
        filename={filePreview?.filename}
        thumbnailUrl={filePreview?.thumbnailUrl}
        onClose={() => setFilePreview(null)}
      />
    </div>
  );
};

export default Messages;